import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { Check } from "lucide-react";
import { FieldRenderer } from "./FieldRenderer";
import type { SectionStructure } from "../utils/buildTabsFromMeta";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { usePatchSaveDoc } from "@/lib/hooks/usePatchSaveDoc";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { MODAL_LINK_FIELD_SEARCH_CLASSES } from "@/lib/styles/modal";
import { useQueryClient } from "@tanstack/react-query";

interface MetaFieldEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Backward-compatible source of fields (legacy).
   * Prefer passing `fields` directly to avoid the modal "guessing".
   */
  section?: SectionStructure;
  /**
   * Preferred: exact fields to render for this modal instance.
   * When provided, the modal renders ONLY these fields.
   */
  fields?: DocFieldSchema[];
  /**
   * Optional title override (e.g., selected group label).
   */
  title?: string;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  /**
   * Optional callback after successful save to sync parent state.
   * Receives the updated document from the server.
   * Use this to update both formData and originalData in the parent.
   */
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  /**
   * Preferred: submit handler that receives a patch of changed fields.
   * Modal will only close on successful submission.
   * @param patch - Object containing only changed fields: { fieldname: newValue }
   * @returns Promise that resolves on success, rejects on error
   */
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  /**
   * @deprecated Use onSubmit instead. Kept for backward compatibility.
   * If provided, will be called after applying field changes (may have stale state issues).
   */
  onSave?: () => void;
  disabled?: boolean;
  isSaving?: boolean;
  documentName?: string; // Document identifier (name/id) - required for attachment fields
  /**
   * Optional width/other overrides for the dialog content. Appended last so it
   * wins over the default width branch (e.g. pass `md:max-w-[80vw]` for a wider
   * modal on data-heavy sections like Selling Defaults + Credit Limit).
   */
  contentClassName?: string;
}

export function MetaFieldEditModal({
  open,
  onOpenChange,
  section,
  fields,
  title,
  formData,
  onFieldChange,
  onSaveSuccess,
  onSubmit,
  disabled,
  isSaving,
  documentName,
  contentClassName,
}: MetaFieldEditModalProps) {

  const [localFormData, setLocalFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);



  // Get doctype from context
  const { doctype: contextDoctype } = useDynamicView();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;

    return {
      editFields: getUI("modal_edit_fields", "Edit Fields"),
      updateInformationBelow: getUI("modal_update_information_below", "Update the information below"),
      save: getUI("modal_save", "Save"),
      saving: getUI("modal_saving", "Saving..."),
      saveChanges: getUI("modal_save_changes", "Save changes"),
      saveCreateFirstShort: getUI("modal_save_create_first_short", "Please save/create this document first"),
      saveCreateFirstLong: getUI(
        "modal_save_create_first_long",
        "Please save/create this document first before editing in modal."
      ),
      saveFailedNoHandler: getUI(
        "modal_save_failed_no_handler",
        "Save failed: No save handler available. Please report this error."
      ),
    };
  }, [language]);

  // R5: New doc handling - check if we can save directly
  const canSaveDirectly = !!contextDoctype && !!documentName;

  // Memoize allFields to ensure stable reference for useMemo.
  // IMPORTANT: When `fields` is provided, the modal must render ONLY those fields.
  const allFields = useMemo(() => {
    if (fields) return fields;
    return section?.columns.flatMap((column) => column.fields) ?? [];
  }, [fields, section]);

  // Only Attach fields truly require a saved document (for file upload association)
  const requiresSavedDoc = useMemo(() => {
    return allFields.some((f) =>
      ["Attach", "Attach Image"].includes(f.fieldtype || "")
    );
  }, [allFields]);

  // Derive query key for cache updates
  // For Item doctype, use centralized keys; for others, use generic pattern
  const detailKey = useMemo(() => {
    if (contextDoctype === "Item" && documentName) {
      return itemQueryKeys.detail(documentName);
    }
    // Generic pattern for other doctypes: [doctype.toLowerCase(), docname]
    if (contextDoctype && documentName) {
      return [contextDoctype.toLowerCase(), documentName] as const;
    }
    return null;
  }, [contextDoctype, documentName]);

  // Check if modal contains any child table fields (for wider modal width)
  const hasChildTable = useMemo(() => {
    // Get all fields from either direct fields prop or from section columns
    const allFieldsList = fields || (section ? section.columns.flatMap((column) => column.fields) : []);
    return allFieldsList.some(
      (field: DocFieldSchema) => field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect'
    );
  }, [fields, section]);

  // Direct save hook (Approach A - bypasses broken prop chain)
  // Only initialize if we have both doctype and docname
  const directSaveMutation = usePatchSaveDoc({
    doctype: contextDoctype || "",
    docname: documentName || "",
    detailKey: detailKey || [],
  });

  // Initialize local form data when modal opens
  useEffect(() => {
    if (open) {
      setLocalFormData({ ...formData });
      setSubmitError(null);
    }
  }, [open, formData]);

  const handleFieldChange = (fieldname: string, value: any) => {
    setLocalFormData((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
    setSubmitError(null); // Clear error when user makes changes
  };

  /**
   * Compute patch: only editable fields that changed.
   * Filters out read-only fields to prevent sending immutable fields
   * (e.g., item_code naming field) that the server will reject,
   * which would trigger echo validation failure in usePatchSaveDoc.
   */
  const computePatch = (): Record<string, any> => {
    const patch: Record<string, any> = {};
    // Build set of editable field names from the modal's field list
    const editableFields = new Set(
      allFields
        .filter((f) => !fieldReadOnly[f.fieldname] && f.read_only !== 1)
        .map((f) => f.fieldname)
    );
    for (const fieldname of Object.keys(localFormData)) {
      // Only include fields that are rendered in this modal AND are editable
      if (!editableFields.has(fieldname)) continue;
      // Use deep comparison for arrays/objects (child tables),
      // reference comparison for scalars
      const local = localFormData[fieldname];
      const original = formData[fieldname];
      const changed = (Array.isArray(local) || (local && typeof local === 'object'))
        ? JSON.stringify(local) !== JSON.stringify(original)
        : local !== original;
      if (changed) {
        patch[fieldname] = local;
      }
    }
    return patch;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || disabled) return;

    const patch = computePatch();

    // If no changes, just close
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // R1: Approach A - Direct save (bypasses broken prop chain)
      // This is the preferred path when we have doctype + docname
      if (canSaveDirectly && detailKey) {
        const updatedDoc = await directSaveMutation.mutateAsync(patch);
        
        // CRITICAL: Sync parent formData with saved changes
        // The cache is updated, but parent component's formData state needs to be synced too
        // Apply patch to parent via onFieldChange so UI updates immediately
        Object.keys(patch).forEach((fieldname) => {
          onFieldChange(fieldname, patch[fieldname]);
        });
        
        // CRITICAL: Update cache immediately + invalidate for refetch
        // This ensures:
        // 1. Immediate UI update using updatedDoc from server (instant feedback)
        // 2. Follow-up refetch as safety net in case other fields changed elsewhere
        // This is especially important for child table deletions/additions
        if (updatedDoc && detailKey) {
          queryClient.setQueryData(detailKey, updatedDoc);
          queryClient.invalidateQueries({ queryKey: detailKey });
        }
        
        // If parent provided onSaveSuccess callback, call it with updated document
        // This allows parent to update both formData and originalData
        if (onSaveSuccess && updatedDoc) {
          onSaveSuccess(updatedDoc);
        }
        
        // Only close on success
        onOpenChange(false);
        return;
      }

      // Draft mode for new documents
      if (!documentName) {
        // Only block if modal contains attachment fields
        if (requiresSavedDoc) {
          setSubmitError(uiLabels.saveCreateFirstLong);
          return;
        }
        
        // Draft mode: apply changes locally via onFieldChange and close
        if (onFieldChange) {
          Object.keys(patch).forEach((fieldname) => {
            onFieldChange(fieldname, patch[fieldname]);
          });
        }
        onOpenChange(false);
        return;
      }

      // Fallback: use onSubmit prop if provided (Approach B - parent save)
      if (onSubmit) {
        await onSubmit(patch);
        // Only close on success
        onOpenChange(false);
        return;
      }

      // R2: No silent success - if patch exists but no save path available, fail loudly
      const userErrorMessage = uiLabels.saveFailedNoHandler;
      setSubmitError(userErrorMessage);
      throw new Error("Internal: No save path available for modal patch save");
    } catch (error) {
      // G4: Keep modal open on error and show inline error
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes';
      setSubmitError(errorMessage);
      // Don't alert here - error is shown in UI via submitError state
    } finally {
      setIsSubmitting(false);
    }
  };

  // Scroll lock removed — Radix Dialog handles this natively via modal prop.
  // The manual body.style.overflow='hidden' was triggering a synchronous layout
  // reflow on open, adding ~50-100ms perceived delay before the animation started.

  // Compute dependencies for visible fields
  const { visibleFieldNames, fieldReadOnly } = useFieldDependencies(
    allFields,
    localFormData
  );

  // Get view context for label resolution (reuse doctype from above)
  const { mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();

  // Render compact field input (without label)
  const renderFieldInput = (field: DocFieldSchema, value: any, onChange: (value: any) => void) => {
    const computedReadOnly = fieldReadOnly[field.fieldname] ?? field.read_only === 1;
    const isReadOnly = disabled || computedReadOnly;

    switch (field.fieldtype) {
      case "Link": {
        const resolvedLabel = resolveLabel(contextDoctype || "", field, mode, style, enableSystemTranslations);
        return (
          <LinkFieldSearch
            doctype={field.options || ""}
            value={value || null}
            onChange={onChange}
            disabled={isReadOnly}
            placeholder={`Search ${resolvedLabel.label}...`}
            className={MODAL_LINK_FIELD_SEARCH_CLASSES}
            {...({ align: "end" } as any)}
          />
        );
      }

      case "Select":
        const options = field.options?.split("\n").filter(Boolean) || [];
        return (
          <Select value={value || ""} onValueChange={onChange} disabled={isReadOnly}>
            <SelectTrigger className="flex-1 text-right justify-end pr-2 !bg-transparent border-0 p-0 h-auto text-[17px]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "Check":
        return (
          <Switch
            checked={value === 1 || value === true}
            onCheckedChange={(checked) => onChange(checked ? 1 : 0)}
            disabled={isReadOnly}
          />
        );

      case "Int":
      case "Float":
      case "Currency":
      case "Percent":
        return (
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => {
              const numValue = e.target.value === "" ? null : Number(e.target.value);
              onChange(numValue);
            }}
            disabled={isReadOnly}
            className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
          />
        );

      case "Date":
        return (
          <div className="flex-1 text-right">
            <DatePicker
              value={value || undefined}
              onChange={(dateStr) => onChange(dateStr || null)}
              disabled={isReadOnly}
              placeholder="Select date"
              className="w-full"
            />
          </div>
        );

      case "Datetime":
        return (
          <Input
            type="datetime-local"
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={isReadOnly}
            className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
          />
        );

      case "Text":
      case "Small Text":
      case "Data":
        return (
          <Input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isReadOnly}
            className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 placeholder:text-gray-400"
          />
        );

      case "Long Text":
      case "Text Editor":
      case "Code":
        return (
          <Textarea
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isReadOnly}
            rows={4}
            className="w-full !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 resize-none placeholder:text-gray-400"
          />
        );

      case "Table":
      case "Table MultiSelect":
        // For child tables, use FieldRenderer to show full editing UI
        // This prevents [object Object] by rendering the actual table component
        return (
          <div className="w-full">
            <FieldRenderer
              field={field}
              value={value || []}
              onChange={onChange}
              disabled={isReadOnly}
              resetKey={undefined} // Modal doesn't need resetKey resync
              viewMode="system" // Use system mode for full child table UI
              documentName={documentName}
              parentDoc={localFormData}
            />
          </div>
        );

      default:
        return (
          <Input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isReadOnly}
            className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 placeholder:text-gray-400"
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className={cn(
        "max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden",
        hasChildTable ? "max-w-[95vw]" : "max-w-[680px]",
        contentClassName
      )}>
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        {/* Checkmark button - top right */}
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
          disabled={
            isSaving || 
            disabled || 
            isSubmitting || 
            directSaveMutation.isPending ||
            (requiresSavedDoc && !documentName) // Only disable for attachment sections on new docs
          }
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
          title={
            requiresSavedDoc && !documentName
              ? uiLabels.saveCreateFirstShort
              : uiLabels.saveChanges
          }
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">{uiLabels.save}</span>
        </Button>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-4 md:px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                {title || section?.label || uiLabels.editFields}
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                {uiLabels.updateInformationBelow}
              </DialogDescription>
              {requiresSavedDoc && !documentName && (
                <div className="mt-4 px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-sm">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    {uiLabels.saveCreateFirstLong}
                  </p>
                </div>
              )}
              {submitError && (
                <div className="mt-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}
            </DialogHeader>
            
            <div className="pb-8 px-4 md:px-10 pt-2">
              <div className="py-2 space-y-5">{(() => {
                  const visibleFields = allFields.filter((field) => visibleFieldNames.has(field.fieldname));
                  
                  // Separate fields by type
                  const regularFields: typeof visibleFields = [];
                  const textAreaFields: typeof visibleFields = [];
                  const tableFields: typeof visibleFields = [];
                  
                  visibleFields.forEach((field) => {
                    const isTextArea = ['Long Text', 'Text Editor', 'Code'].includes(field.fieldtype || '');
                    const isTable = field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect';
                    
                    if (isTextArea) {
                      textAreaFields.push(field);
                    } else if (isTable) {
                      tableFields.push(field);
                    } else {
                      regularFields.push(field);
                    }
                  });
                  
                  const result: React.ReactNode[] = [];
                  
                  // Render regular fields in a single grouped container (like ItemPriceBasicModal)
                  if (regularFields.length > 0) {
                    result.push(
                      <div key="regular-fields-group" className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="space-y-0">
                          {regularFields.map((field, index) => {
                            const isLast = index === regularFields.length - 1;
                            const resolvedLabel = resolveLabel(contextDoctype || "", field, mode, style, enableSystemTranslations);
                            return (
                              <div
                                key={field.fieldname}
                                className={cn(
                                  "flex items-center justify-between min-h-[52px] px-5",
                                  !isLast && "border-b border-gray-200/40 dark:border-gray-700/40"
                                )}
                              >
                                <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                                  {resolvedLabel.label}
                                </label>
                                <div className="flex-1 max-w-[70%] md:max-w-[60%] flex justify-end">
                                  {renderFieldInput(
                                    field,
                                    localFormData[field.fieldname],
                                    (value) => handleFieldChange(field.fieldname, value)
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  
                  // Render textarea fields in separate containers
                  textAreaFields.forEach((field) => {
                    const resolvedLabel = resolveLabel(contextDoctype || "", field, mode, style, enableSystemTranslations);
                    result.push(
                      <div key={field.fieldname} className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="px-5 py-4 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                            {resolvedLabel.label}
                          </label>
                        </div>
                        <div className="px-5 py-4">
                          {renderFieldInput(
                            field,
                            localFormData[field.fieldname],
                            (value) => handleFieldChange(field.fieldname, value)
                          )}
                        </div>
                      </div>
                    );
                  });
                  
                  // Render table fields in separate containers
                  tableFields.forEach((field) => {
                    const resolvedLabel = resolveLabel(contextDoctype || "", field, mode, style, enableSystemTranslations);
                    result.push(
                      <div key={field.fieldname} className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <div className="px-5 py-4 border-b border-gray-200/40 dark:border-gray-700/40">
                          <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                            {resolvedLabel.label}
                          </label>
                        </div>
                        <div className="px-5 py-4">
                          {renderFieldInput(
                            field,
                            localFormData[field.fieldname],
                            (value) => handleFieldChange(field.fieldname, value)
                          )}
                        </div>
                      </div>
                    );
                  });
                  
                  return result;
                })()}</div>
            </div>
            
            {/* Bottom fade-out gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>
          
          {/* Sticky Save Button */}
          <div className="flex justify-center px-4 md:px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit" 
              disabled={isSaving || disabled || isSubmitting}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving || isSubmitting ? uiLabels.saving : uiLabels.save}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

